/**
 * VAL-TIERS-1-2 — what the owner's purchase includes, and the Tier 1 / Tier 2 downloads.
 *
 * The tier names and lines come from the server (core/valuation/value_payment.TIER_TEXT), so
 * the wording lives in one place. A tier unlocks itself and every tier before it; the server
 * sends that list already resolved (`value_tiers`, lowest first). Owner/super_admin only: a
 * signer's engagement carries no `value_tiers`, so this renders nothing for them.
 *
 * TIER_TEXT / the button label are PROPOSED wording, pending owner approval (VAL-TIERS-1-2 report).
 */
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { downloadTierDocument } from '@/api/valuationB3'
import { PaidDownloadButton } from './_SnapshotDownload'
import type { ValuationEngagement } from '@/types/valuation'

export const TIER_TEXT = {
  title: 'What you bought',
  includes: 'It includes:',
  download: 'Download {name}',
} as const

/** The tiers that have their own document here; the Snapshot keeps its own button. */
const TIER_DOCUMENTS = new Set(['glance', 'value_drivers'])

export function TierDownloadButton({ engagementId, tier, name }: { engagementId: string; tier: string; name: string }) {
  return <PaidDownloadButton variant="outline" label={TIER_TEXT.download.replace('{name}', name)}
                             fetch={() => downloadTierDocument(engagementId, tier)} />
}

/** The Tier 1 / Tier 2 download buttons the purchase unlocks (none for a Snapshot-only list). */
export function TierDownloads({ engagement }: { engagement: ValuationEngagement }) {
  const docs = (engagement.value_tiers ?? []).filter(t => TIER_DOCUMENTS.has(t.tier))
  if (docs.length === 0) return null
  return (
    <div className="flex flex-col gap-2 sm:w-64">
      {docs.map(t => (
        <TierDownloadButton key={t.tier} engagementId={engagement.valuation_engagement_id}
                            tier={t.tier} name={t.title} />
      ))}
    </div>
  )
}

/** On "Your value": the bought tier and everything it includes. No downloads here. */
export function TierIncludes({ engagement }: { engagement: ValuationEngagement }) {
  const tiers = engagement.value_tiers ?? []
  const top = tiers.find(t => t.tier === engagement.value_tier) ?? tiers[tiers.length - 1]
  if (!top) return null
  return (
    <Card data-testid="tier-includes">
      <CardHeader><CardTitle>{TIER_TEXT.title}</CardTitle></CardHeader>
      <CardContent className="space-y-2 text-sm text-pal-ink-body">
        <p><strong>{top.title}</strong></p>
        <p>{TIER_TEXT.includes}</p>
        <ul className="list-disc space-y-1 pl-5">
          {tiers.map(t => (
            <li key={t.tier}><span className="font-medium">{t.title}</span>{': '}<span>{t.description}</span></li>
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}
